import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EnquiryDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enquiry = location?.state;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!enquiry || !enquiry?.from || !enquiry?.to) {
      navigate('/route-selection');
    }
  }, [enquiry, navigate]);

  const handleSendToWhatsApp = () => {
    let whatsappMessage = `*Enquiry Details*\n\n`;
    whatsappMessage += `*Route:* ${enquiry?.from} to ${enquiry?.to}\n`;
    whatsappMessage += `*Vehicle:* ${enquiry?.vehicle?.name || enquiry?.vehicle?.type || 'Not specified'}\n`;
    whatsappMessage += `*Price:* ₹${enquiry?.price?.toLocaleString('en-IN')} Inc. of Taxes\n`;
    
    if (enquiry?.email) {
      whatsappMessage += `*Email:* ${enquiry?.email}\n`;
    }
    
    if (enquiry?.message) {
      whatsappMessage += `*Message:* ${enquiry?.message}\n`;
    }
    
    whatsappMessage += `\nPlease confirm availability and booking details.`;
    
    window.open(`https://wa.me/917998666660?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (!enquiry) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1">
        <div className="bg-gradient-journey text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-white" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
              Enquiry Submitted Successfully!
            </h1>
            <p className="text-lg md:text-xl text-white/90">
              Thank you for your enquiry. We'll get back to you soon.
            </p>
          </div>
        </div>

        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-card rounded-xl border border-border p-6 md:p-8 space-y-8">
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                  Your Enquiry Details
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                    <Icon name="MapPin" size={24} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-1">Route</p>
                      <p className="text-lg font-semibold text-foreground">
                        {enquiry?.from} → {enquiry?.to}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                    <Icon name="Car" size={24} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-1">Selected Vehicle</p>
                      <p className="text-lg font-semibold text-foreground">
                        {enquiry?.vehicle?.name || enquiry?.vehicle?.type || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                    <Icon name="IndianRupee" size={24} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-1">Price</p>
                      <p className="text-2xl font-bold text-primary">
                        ₹{enquiry?.price?.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Inc. of Taxes*</p>
                    </div>
                  </div>
                  
                  {enquiry?.email && (
                    <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                      <Icon name="Mail" size={24} className="text-primary mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-1">Email</p>
                        <p className="text-base font-semibold text-foreground">
                          {enquiry?.email}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {enquiry?.message && (
                    <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
                      <Icon name="MessageSquare" size={24} className="text-primary mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground mb-1">Message</p>
                        <p className="text-base text-foreground whitespace-pre-wrap">
                          {enquiry?.message}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t border-border pt-6">
                <div className="bg-success/10 border border-success/20 rounded-lg p-6 mb-6">
                  <div className="flex items-start gap-4">
                    <Icon name="MessageCircle" size={28} className="text-success mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        Send Enquiry to WhatsApp
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Get instant confirmation and faster response by sending your enquiry directly to our WhatsApp.
                      </p>
                      <Button
                        variant="default"
                        size="lg"
                        iconName="MessageCircle"
                        iconPosition="left"
                        onClick={handleSendToWhatsApp}
                        className="bg-success hover:bg-success/90"
                      >
                        Send to WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="text-center space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Our team will contact you within 24 hours to confirm your booking.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="Phone"
                      iconPosition="left"
                      onClick={() => window.open('tel:+917998666660', '_self')}
                    >
                      Call +91-79986-66660
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="Home"
                      iconPosition="left"
                      onClick={handleBackToHome}
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default EnquiryDetails;